"use client";

import { autoFuseNeedsReview } from "@/lib/terminal-fuse-limit";
import type { CableSpec } from "@/lib/cable-current";
import type { CanvasFuseLabel } from "./canvas-fuse-label";
import {
  Background,
  BackgroundVariant,
  ConnectionMode,
  Controls,
  MiniMap,
  ReactFlow,
  useReactFlow,
  type Edge,
  type EdgeChange,
  type Node,
  type NodeChange,
  type ReactFlowInstance,
} from "@xyflow/react";
import {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { getDefinition } from "@/lib/catalog";
import { absolutePosition, isFuseHolderVariant } from "@/lib/auto-fuse";
import { GEOMETRY } from "@/lib/canvas-geometry";
import { effectiveDimensions } from "@/lib/effective";
import {
  applyEdgeSelectionChanges,
  applyNodeSelectionChanges,
} from "@/lib/selection-changes";
import { useSystemStore } from "@/lib/store";
import { endpointPos } from "@/lib/store-helpers";
import type {
  Cable,
  ComponentNode as ComponentNodeT,
  System,
  SystemNode,
  ZoneNode as ZoneNodeT,
} from "@/lib/types";
import { isJunction } from "@/lib/node-kind";
import { computeEnergizedTerminals } from "@/lib/diagnostics/graph";
import { indexNodesById } from "@/lib/node-tree";
import {
  useBatteryBankCurrents,
  useBusNominalVoltage,
  useCableDemands,
  useCableSpecGetter,
} from "@/components/hooks/use-graph-derived";
import { useActiveTerminals } from "@/lib/canvas-active-terminals";
import { useCanvasDrag } from "@/lib/canvas-drag";
import { useCanvasHover } from "@/lib/canvas-hover";
import { runAllDiagnostics } from "@/lib/diagnostics";
import { markEnd, markStart } from "@/lib/perf-marks";
import { useAutoSizeVdropTarget } from "@/lib/settings/auto-size-vdrop-target";
import { useCableRouter } from "@/components/hooks/use-cable-router";
import type { RoutingScene } from "@/lib/cable-router/types";
import type { BlueprintModel } from "@/lib/blueprint";
import { CableEdgeView, type CableEdgeData } from "./cable-edge";
import {
  CanvasMetricPillsProvider,
  CanvasRenderProfileProvider,
} from "./canvas-mode";
import { CableConnectionLine } from "./connection-line";
import { ConnectionPreviewContext, RoutingSceneContext } from "./connection-preview";
import { ComponentNodeView } from "./component-node";
import { JunctionNodeView } from "./junction-node";
import { CanvasEmptyState } from "./canvas-empty-state";
import { AssistantEditReveal } from "./assistant-edit-reveal";
import { GeneratedSystemBanner } from "./generated-system-banner";
import { PanLockOverlay } from "./pan-lock-overlay";
import { useCanvasConnect } from "./use-canvas-connect";
import { useCanvasDrop } from "./use-canvas-drop";
import { useCanvasKeyboard } from "./use-canvas-keyboard";
import { ZoneNodeView } from "./zone-node";
import { useCatalogText } from "@/components/hooks/use-catalog-text";
import { getCatalogText } from "@/lib/i18n/catalog-text";
import { useI18n } from "@/lib/i18n/store";
import { localizedName, useT } from "@/lib/i18n";
import { useCommentPopover } from "@/components/comments/popover-store";
import { useIsTouchLayout } from "@/lib/use-is-touch-layout";
import {
  canvasPanOnDrag,
  canvasSelectionOnDrag,
  flowHitTolerance,
  touchHandleOutwardSlop,
  touchHandleSlop,
} from "@/lib/canvas-interaction";
import {
  touchCentroid,
  touchDistance,
  viewportForPinch,
} from "@/lib/touch-canvas-gesture";

const nodeTypes = {
  component: ComponentNodeView,
  zone: ZoneNodeView,
  junction: JunctionNodeView,
};

const edgeTypes = {
  cable: CableEdgeView,
};

const { JUNCTION_HALF } = GEOMETRY;

type Pt = { x: number; y: number };

function nodeCenterAbs(node: SystemNode, nodes: SystemNode[]): Pt | null {
  const abs = absolutePosition(node, nodes);
  if (node.kind === "zone") {
    return { x: abs.x + node.width / 2, y: abs.y + node.height / 2 };
  }
  const def = getDefinition(node.definitionId, node.customDefinition);
  if (isJunction(node, def)) {
    return { x: abs.x + JUNCTION_HALF, y: abs.y + JUNCTION_HALF };
  }
  if (!def) return null;
  const dims = effectiveDimensions(def, node);
  return { x: abs.x + dims.width / 2, y: abs.y + dims.height / 2 };
}

function pickJunctionSide(junctionAbs: Pt, other: Pt): "top" | "right" | "bottom" | "left" {
  const dx = other.x - junctionAbs.x;
  const dy = other.y - junctionAbs.y;
  if (Math.abs(dx) >= Math.abs(dy)) return dx >= 0 ? "right" : "left";
  return dy >= 0 ? "bottom" : "top";
}

/** Includes cable waypoints: a routed cable can run past every node, which fitView can't see. */
function blueprintContentBounds(system: System) {
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  const includeRect = (x: number, y: number, width = 0, height = 0) => {
    if (![x, y, width, height].every(Number.isFinite)) return;
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x + width);
    maxY = Math.max(maxY, y + height);
  };

  for (const node of system.nodes) {
    const position = absolutePosition(node, system.nodes);
    if (node.kind === "zone") {
      includeRect(position.x, position.y, node.width, node.height);
      continue;
    }
    const def = getDefinition(node.definitionId, node.customDefinition);
    if (isJunction(node, def)) {
      includeRect(position.x, position.y, JUNCTION_HALF * 2, JUNCTION_HALF * 2);
    } else if (def) {
      const dimensions = effectiveDimensions(def, node);
      includeRect(position.x, position.y, dimensions.width, dimensions.height);
    } else {
      includeRect(position.x, position.y, 40, 40);
    }
  }
  for (const cable of system.cables) {
    for (const waypoint of cable.waypoints ?? []) {
      includeRect(waypoint.x, waypoint.y);
    }
  }

  if (![minX, minY, maxX, maxY].every(Number.isFinite)) return null;
  const margin = 72;
  return {
    x: minX - margin,
    y: minY - margin,
    width: Math.max(1, maxX - minX + margin * 2),
    height: Math.max(1, maxY - minY + margin * 2),
  };
}

export type CanvasPresentation = "editor" | "blueprint";

function CanvasInner({
  viewOnly = false,
  canStartSystem = false,
  embedded = false,
  showControls = true,
  autoFit = true,
  showMetricsOnTouch = false,
  showBatteryDetails = true,
  connectionOverview = false,
  hideCableLabels = false,
  initialFitPadding = 0.2,
  initialFitMaxZoom = 1.1,
  minZoom = 0.2,
  ariaLabel,
  elementsFocusable = true,
  controlsPosition = "bottom-right",
  presentation = "editor",
  blueprintModel,
}: {
  viewOnly?: boolean;
  canStartSystem?: boolean;
  embedded?: boolean;
  showControls?: boolean;
  autoFit?: boolean;
  showMetricsOnTouch?: boolean;
  showBatteryDetails?: boolean;
  connectionOverview?: boolean;
  hideCableLabels?: boolean;
  initialFitPadding?: number;
  initialFitMaxZoom?: number;
  minZoom?: number;
  ariaLabel?: string;
  elementsFocusable?: boolean;
  controlsPosition?: "top-left" | "top-right" | "bottom-left" | "bottom-right";
  presentation?: CanvasPresentation;
  blueprintModel?: BlueprintModel;
}) {
  const tr = useT();
  const locale = useI18n((state) => state.locale);
  useCatalogText(locale);
  // English labels live on the definitions. Description-only and unrelated
  // locale arrivals must not rebuild nodes and reset React Flow's handle bounds.
  const catalogLabels = locale === "en" ? undefined : getCatalogText(locale);
  // useT returns a new callback each render. Depending on that callback rebuilds
  // the nodes on every cable hover, resetting React Flow's measured handles and
  // unmounting the very edge that owns the hover state.
  const zoneFallbackLabel = tr("zone.fallback_label");
  const fuseReviewLabel = tr("cable.needs_review");
  const {
    fitBounds,
    fitView,
    getNodes,
    getInternalNode,
    getViewport,
    screenToFlowPosition,
    setViewport,
  } = useReactFlow();
  const blueprint = presentation === "blueprint";
  const isTouchLayout = useIsTouchLayout();
  const canvasRootRef = useRef<HTMLDivElement>(null);
  const [touchZoom, setTouchZoom] = useState(1);
  const {
    cableTargetId,
    cableTargetEdgeId,
    connectionPreview,
    cancelConnectionGesture,
    onConnectStart,
    onConnect,
    onConnectEnd,
  } = useCanvasConnect();

  useEffect(() => {
    const root = canvasRootRef.current;
    if (!root || !isTouchLayout || blueprint || embedded) return;

    let blockedByResizeControl = false;
    let gestureSession = false;
    let pinch:
      | {
          startDistance: number;
          startZoom: number;
          flowAnchor: { x: number; y: number };
          rendererOrigin: { x: number; y: number };
        }
      | null = null;

    const touchPoints = (touches: TouchList) =>
      Array.from(touches, (touch) => ({
        x: touch.clientX,
        y: touch.clientY,
      }));

    const onTouchStart = (event: TouchEvent) => {
      if (event.touches.length === 1) {
        blockedByResizeControl =
          event.target instanceof Element &&
          Boolean(event.target.closest(".react-flow__resize-control"));
      }
      if (event.touches.length < 2) return;

      // A second finger changes the user's intent from wiring to viewport
      // navigation. Cancel even before XYFlow's drag threshold is crossed, then
      // keep the original touchend from committing a provisional junction.
      cancelConnectionGesture();
      if (blockedByResizeControl) return;

      event.preventDefault();
      event.stopPropagation();
      if (pinch) return;
      gestureSession = true;

      const points = touchPoints(event.touches);
      const centroid = touchCentroid(points);
      const renderer = root.querySelector<HTMLElement>(".react-flow__renderer");
      const bounds = renderer?.getBoundingClientRect();
      const viewport = getViewport();
      pinch = {
        startDistance: Math.max(touchDistance(points), 1),
        startZoom: viewport.zoom,
        flowAnchor: screenToFlowPosition(centroid, { snapToGrid: false }),
        rendererOrigin: { x: bounds?.left ?? 0, y: bounds?.top ?? 0 },
      };
    };

    const onTouchMove = (event: TouchEvent) => {
      if (!gestureSession) return;
      event.preventDefault();
      event.stopPropagation();
      if (!pinch || event.touches.length < 2) return;

      const points = touchPoints(event.touches);

      const zoom = Math.min(
        3,
        Math.max(
          0.2,
          pinch.startZoom *
            (touchDistance(points) / pinch.startDistance),
        ),
      );
      void setViewport(
        viewportForPinch({
          flowAnchor: pinch.flowAnchor,
          centroid: touchCentroid(points),
          rendererOrigin: pinch.rendererOrigin,
          zoom,
        }),
        { duration: 0 },
      );
    };

    const onTouchEnd = (event: TouchEvent) => {
      // Let the event reach d3 so a node/pane drag that saw the first touch
      // can release its internal capture and dragging state.
      if (event.touches.length < 2) pinch = null;
      if (event.touches.length > 0) return;
      pinch = null;
      gestureSession = false;
      blockedByResizeControl = false;
    };

    root.addEventListener("touchstart", onTouchStart, {
      capture: true,
      passive: false,
    });
    root.addEventListener("touchmove", onTouchMove, {
      capture: true,
      passive: false,
    });
    root.addEventListener("touchend", onTouchEnd, true);
    root.addEventListener("touchcancel", onTouchEnd, true);
    return () => {
      root.removeEventListener("touchstart", onTouchStart, true);
      root.removeEventListener("touchmove", onTouchMove, true);
      root.removeEventListener("touchend", onTouchEnd, true);
      root.removeEventListener("touchcancel", onTouchEnd, true);
    };
  }, [
    blueprint,
    cancelConnectionGesture,
    embedded,
    getViewport,
    isTouchLayout,
    screenToFlowPosition,
    setViewport,
  ]);

  const system = useSystemStore((s) => s.system);
  const blueprintCableById = useMemo(
    () => new Map((blueprintModel?.cables ?? []).map((cable) => [cable.cableId, cable])),
    [blueprintModel],
  );
  const mode = useSystemStore((s) => s.mode);
  const simulateComponentStates = useSystemStore(
    (s) => s.simulateState.componentStates,
  );
  const simulateLoadStates = useSystemStore(
    (s) => s.simulateState.loadStates,
  );
  const autoSizeTargetPct = useAutoSizeVdropTarget((s) => s.target);
  const selection = useSystemStore((s) => s.selection);
  const setSelection = useSystemStore((s) => s.setSelection);
  const setNodePosition = useSystemStore((s) => s.setNodePosition);
  const dragNodePosition = useSystemStore((s) => s.dragNodePosition);
  const finalizeNodeDrag = useSystemStore((s) => s.finalizeNodeDrag);
  const reparentNodesToZones = useSystemStore((s) => s.reparentNodesToZones);
  const removeNodes = useSystemStore((s) => s.removeNodes);
  const removeCables = useSystemStore((s) => s.removeCables);
  const updateNode = useSystemStore((s) => s.updateNode);
  const tidyCableLayoutsForNodes = useSystemStore((s) => s.tidyCableLayoutsForNodes);

  const { onDrop, onDragOver } = useCanvasDrop();
  useCanvasKeyboard(viewOnly || blueprint);

  // Skipped when empty: React Flow would auto-center on the first dropped node,
  // making the drop position look ignored. The retry waits for measurement —
  // fitView silently no-ops until node dimensions exist — and then for the
  // measurements to hold still: a parent effect runs after this one, so a
  // caller that lights product images or metric pills on mount grows the cards
  // a frame later, and a fit taken before that lands the system off-centre.
  const didInitialFitRef = useRef(false);
  useEffect(() => {
    if (!autoFit) return;
    if (didInitialFitRef.current) return;
    if (blueprint) return;
    if (system.nodes.length === 0) return;
    let cancelled = false;
    let frames = 0;
    let lastBounds = "";
    let settledFrames = 0;
    const attempt = () => {
      if (cancelled || didInitialFitRef.current) return;
      const visibleNodes = getNodes().filter((node) => !node.hidden);
      const allNodesMeasured =
        visibleNodes.length > 0 &&
        visibleNodes.every(
          (node) => node.measured?.width && node.measured?.height,
        );
      const bounds = visibleNodes
        .map(
          (node) =>
            `${node.id}:${node.position.x},${node.position.y}:${node.measured?.width}x${node.measured?.height}`,
        )
        .join("|");
      settledFrames = allNodesMeasured && bounds === lastBounds ? settledFrames + 1 : 0;
      lastBounds = bounds;
      if (settledFrames >= 2 || frames >= 60) {
        didInitialFitRef.current = true;
        void fitView({
          padding: initialFitPadding,
          maxZoom: initialFitMaxZoom,
          minZoom,
        });
        return;
      }
      frames += 1;
      requestAnimationFrame(attempt);
    };
    attempt();
    return () => {
      cancelled = true;
    };
  }, [
    autoFit,
    system.nodes.length,
    fitView,
    getNodes,
    initialFitMaxZoom,
    initialFitPadding,
    minZoom,
    blueprint,
  ]);

  const handleInit = useCallback(
    (instance: ReactFlowInstance) => {
      const root = canvasRootRef.current;
      if (root) {
        const zoom = instance.getZoom();
        setTouchZoom(zoom);
        root.style.setProperty(
          "--touch-hit-slop",
          `${touchHandleSlop(zoom)}px`,
        );
        root.style.setProperty(
          "--touch-hit-outward-slop",
          `${touchHandleOutwardSlop(zoom)}px`,
        );
      }
      if (!blueprint) return;
      const bounds = blueprintContentBounds(system);
      if (!bounds) return;
      window.requestAnimationFrame(() => {
        void instance.fitBounds(bounds, { padding: 0.04, duration: 0 });
      });
    },
    [blueprint, system],
  );

  const handleViewportMove = useCallback(
    (_event: MouseEvent | TouchEvent | null, viewport: { zoom: number }) => {
      canvasRootRef.current?.style.setProperty(
        "--touch-hit-slop",
        `${touchHandleSlop(viewport.zoom)}px`,
      );
      canvasRootRef.current?.style.setProperty(
        "--touch-hit-outward-slop",
        `${touchHandleOutwardSlop(viewport.zoom)}px`,
      );
    },
    [],
  );

  const handleViewportMoveEnd = useCallback(
    (_event: MouseEvent | TouchEvent | null, viewport: { zoom: number }) => {
      setTouchZoom(viewport.zoom);
    },
    [],
  );

  useEffect(() => {
    if (!blueprint || !canvasRootRef.current) return;
    const bounds = blueprintContentBounds(system);
    if (!bounds) return;
    let frame = 0;
    const refit = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        void fitBounds(bounds, { padding: 0.04, duration: 0 });
      });
    };
    const observer = new ResizeObserver(refit);
    observer.observe(canvasRootRef.current);
    return () => {
      observer.disconnect();
      window.cancelAnimationFrame(frame);
    };
  }, [blueprint, fitBounds, system]);

  // Without this, a load past an open switch still reports its static ampDraw.
  // Edit mode keeps those static reads on purpose — it sizes for worst case.
  const simulateEnergized = useMemo(
    () =>
      mode === "simulate" && !blueprint
        ? computeEnergizedTerminals(system, getDefinition, {
            componentStates: simulateComponentStates,
            loadStates: simulateLoadStates,
          })
        : undefined,
    [system, mode, simulateComponentStates, simulateLoadStates, blueprint],
  );

  const cableDemandSimulateCtx = useMemo(
    () =>
      mode === "simulate" && !blueprint
        ? {
            componentStates: simulateComponentStates,
            loadStates: simulateLoadStates,
            energizedTerminals: simulateEnergized,
          }
        : undefined,
    [mode, simulateComponentStates, simulateLoadStates, simulateEnergized, blueprint],
  );

  const cableDemands = useCableDemands(system, cableDemandSimulateCtx);

  const computeSpec = useCableSpecGetter(system, autoSizeTargetPct, cableDemandSimulateCtx);
  // The same immutable cable is drawn at its endpoints and on its edge. Share
  // one calculation per cable for this snapshot, including while dragging.
  const specOf = useMemo(() => {
    const cache = new WeakMap<Cable, CableSpec>();
    return (cable: Cable) => {
      const cached = cache.get(cable);
      if (cached) return cached;
      const spec = computeSpec(cable);
      cache.set(cable, spec);
      return spec;
    };
  }, [computeSpec]);

  // Zones are pushed first so React Flow can resolve their children's parentId.
  const rfNodes = useMemo<Node[]>(() => {
    const selectedIds = new Set(selection.nodeIds);
    // Drop a parentId whose zone is gone: React Flow's parent lookup misses and warns.
    const knownNodeIds = new Set(system.nodes.map((n) => n.id));
    const slotFusesByNode = new Map<string, Record<string, CanvasFuseLabel>>();
    const addRating = (nodeId: string, terminalId: string, fuse: CanvasFuseLabel) => {
      let m = slotFusesByNode.get(nodeId);
      if (!m) {
        m = {};
        slotFusesByNode.set(nodeId, m);
      }
      m[terminalId] = fuse;
    };
    for (const c of system.cables) {
      const row = blueprint ? blueprintCableById.get(c.id) : undefined;
      const rating = blueprint ? row?.fuseAmp : c.fuseAmpOverride;
      if (rating == null) continue;
      const review = blueprint ? row?.fuseNeedsReview : c.fuseAmpAuto && autoFuseNeedsReview(c, specOf(c));
      const fuse = { rating, review: review ? fuseReviewLabel : undefined };
      addRating(c.fromNodeId, c.fromTerminalId, fuse);
      addRating(c.toNodeId, c.toTerminalId, fuse);
    }
    for (const n of system.nodes) {
      if (n.kind !== "component" || (!n.batteryFuse && !n.fuseAmp)) continue;
      const def = getDefinition(n.definitionId, n.customDefinition);
      if (!def) continue;
      for (const t of def.terminals) {
        if (n.batteryFuse && def.category === "battery" && t.polarity === "positive") {
          addRating(n.id, t.id, { rating: n.batteryFuse.ratingA });
        } else if (n.fuseAmp != null && n.fuseAmp > 0 && isFuseHolderVariant(def) &&
          (t.direction === "input" || t.direction === "output")) {
          addRating(n.id, t.id, { rating: n.fuseAmp });
        }
      }
    }
    const nodes: Node[] = [];
    for (const n of system.nodes) {
      if (n.kind === "zone") {
        const z = n as ZoneNodeT;
        nodes.push({
          id: z.id,
          type: "zone",
          position: z.position,
          data: { label: z.label },
          width: z.width,
          height: z.height,
          selectable: !blueprint,
          draggable: !viewOnly && !blueprint,
          selected: !blueprint && selectedIds.has(z.id),
          ariaLabel: z.label || zoneFallbackLabel,
        });
      }
    }
    for (const n of system.nodes) {
      if (n.kind !== "component") continue;
      const c = n as ComponentNodeT;
      const cDef = getDefinition(c.definitionId, c.customDefinition);
      if (cDef?.renderVariant === "junction") {
        const node: Node = {
          id: c.id,
          type: "junction",
          position: c.position,
          data: { cableTarget: cableTargetId === c.id, provisional: c.provisional },
          selected: !blueprint && selectedIds.has(c.id),
          ariaLabel:
            c.label || (cDef ? localizedName(cDef, locale) : "Junction"),
          zIndex: 4500,
        };
        if (c.parentId && knownNodeIds.has(c.parentId)) node.parentId = c.parentId;
        nodes.push(node);
        continue;
      }
      const node: Node = {
        id: c.id,
        type: "component",
        position: c.position,
        data: {
          definitionId: c.definitionId,
          customDefinition: c.customDefinition,
          label: c.label,
          terminalFuses: slotFusesByNode.get(c.id),
          terminalOverrides: c.terminalOverrides,
          terminalLabels: c.terminalLabels,
          rotation: c.rotation,
          cableTarget: cableTargetId === c.id,
          studCount: c.studCount,
          isFusebox: c.isFusebox,
          batteryFuse: c.batteryFuse,
          fuseAmp: c.fuseAmp,
        },
        selected: !blueprint && selectedIds.has(c.id),
        ariaLabel:
          c.label || (cDef ? localizedName(cDef, locale) : "Component"),
        // Above cables (2000) and React Flow's ~4100 edge lift, under EdgeLabelRenderer (5000).
        zIndex: 4500,
      };
      // No parent extent — drag-stop reparents by drop position, so children must leave the zone.
      if (c.parentId && knownNodeIds.has(c.parentId)) {
        node.parentId = c.parentId;
      }
      nodes.push(node);
    }
    // React Flow treats a rebuilt node without `measured` as uninitialized and
    // removes its edges until the next measurement. Cable edits rebuild this
    // list too, so retain geometry to keep the captured wire mounted.
    for (const node of nodes) {
      const measured = getInternalNode(node.id)?.measured;
      if (measured?.width != null && measured?.height != null) {
        node.measured = { ...measured };
      }
    }
    return nodes;
  // Catalog label accessors read only the active locale's label cache.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    system.nodes,
    system.cables,
    selection.nodeIds,
    cableTargetId,
    viewOnly,
    blueprint,
    blueprintCableById,
    locale,
    catalogLabels,
    zoneFallbackLabel,
    fuseReviewLabel,
    specOf,
    getInternalNode,
  ]);

  // Signed bank current is used for flow direction, independently of sizing.
  const batteryBank = useBatteryBankCurrents(system, cableDemandSimulateCtx);

  const busNominalV = useBusNominalVoltage(system);

  const nodesById = useMemo(() => indexNodesById(system.nodes), [system.nodes]);

  // Reads system from the store after a mutation. State sweeps are computed
  // when requested by the inspector: speculative sweeps block every drag stop.
  const warmHeavyCaches = useCallback(() => {
    if (useCanvasDrag.getState().isDragging) return;
    const sys = useSystemStore.getState().system;
    void runAllDiagnostics(
      sys,
      getDefinition,
      undefined,
      undefined,
      autoSizeTargetPct,
    );
  }, [autoSizeTargetPct]);

  useEffect(() => {
    // BlueprintModel already owns the document's calculations; do not repeat
    // diagnostic work in the preview and print canvases.
    if (blueprint) return;
    const id = window.setTimeout(() => {
      warmHeavyCaches();
    }, 200);
    return () => window.clearTimeout(id);
  }, [blueprint, system, warmHeavyCaches]);

  const hoverCableId = useCanvasHover((s) => s.hoverCableId);
  const deferredSelectionCableIds = useDeferredValue(selection.cableIds);
  const deferredHoverCableId = useDeferredValue(hoverCableId);
  const activeTerminalsByNode = useMemo(() => {
    const m = new Map<string, Set<string>>();
    const add = (nodeId: string, terminalId: string) => {
      let set = m.get(nodeId);
      if (!set) {
        set = new Set();
        m.set(nodeId, set);
      }
      set.add(terminalId);
    };
    const activeCableIds = new Set(deferredSelectionCableIds);
    if (deferredHoverCableId) activeCableIds.add(deferredHoverCableId);
    if (activeCableIds.size === 0) return m;
    for (const c of system.cables) {
      if (!activeCableIds.has(c.id)) continue;
      add(c.fromNodeId, c.fromTerminalId);
      add(c.toNodeId, c.toTerminalId);
    }
    return m;
  }, [deferredSelectionCableIds, deferredHoverCableId, system.cables]);
  useEffect(() => {
    useActiveTerminals.getState().setByNode(activeTerminalsByNode);
  }, [activeTerminalsByNode]);

  // Node movement can reuse cable data. Wire/terminal reshaping must still
  // publish its new geometry while the electrical hooks hold their last result.
  const isNodeDragging = useCanvasDrag((s) => s.draggedNodeIds.size > 0);
  const cableEdgeDataByIdRef = useRef<Map<string, CableEdgeData> | null>(null);
  const deferredCables = useDeferredValue(system.cables);
  const deferredNodes = useDeferredValue(system.nodes);
  const deferredNodesById = useDeferredValue(nodesById);
  const deferredCableDemands = useDeferredValue(cableDemands);
  const deferredSpecOf = useDeferredValue(specOf);
  const deferredSimulateEnergized = useDeferredValue(simulateEnergized);
  const deferredBatteryBank = useDeferredValue(batteryBank);
  const deferredBusNominalV = useDeferredValue(busNominalV);
  const cableRouter = useCableRouter();
  const routingSceneRef = useRef<RoutingScene | null>(null);
  const previewObstacles = useMemo(() => {
    // A drag freezes edge data. Freeze its obstacle preparation too: rebuilding
    // geometry that no edge will consume still costs every pointer frame.
    if (isNodeDragging && routingSceneRef.current) return routingSceneRef.current;
    const scene = cableRouter.prepare(deferredNodes);
    routingSceneRef.current = scene;
    return scene;
  }, [cableRouter, deferredNodes, isNodeDragging]);
  const cableEdgeDataById = useMemo(() => {
    if (isNodeDragging && cableEdgeDataByIdRef.current) {
      return cableEdgeDataByIdRef.current;
    }
    const perfId = markStart("cableEdgeDataById");
    const m = new Map<string, CableEdgeData>();
    for (const c of deferredCables) {
      m.set(c.id, {
        cable: c,
        cableTarget: cableTargetEdgeId === c.id,
        demand: deferredCableDemands.get(c.id),
        spec: deferredSpecOf(c),
        bankCurrentA: deferredBatteryBank.byCable.get(c.id),
        busNominalV: deferredBusNominalV,
        simulateEnergized: deferredSimulateEnergized,
        nodes: deferredNodes,
        nodesById: deferredNodesById,
        cables: deferredCables,
        previewObstacles,
        blueprint,
        blueprintCable: blueprint ? blueprintCableById.get(c.id) : undefined,
      });
    }
    cableEdgeDataByIdRef.current = m;
    markEnd(perfId, "cableEdgeDataById");
    return m;
  }, [
    deferredNodes,
    deferredCables,
    deferredNodesById,
    previewObstacles,
    cableTargetEdgeId,
    deferredCableDemands,
    deferredSpecOf,
    deferredBatteryBank,
    deferredBusNominalV,
    deferredSimulateEnergized,
    blueprint,
    blueprintCableById,
    isNodeDragging,
  ]);

  const rfEdges = useMemo<Edge[]>(() => {
    const selectedIds = new Set(selection.cableIds);
    const nodes = system.nodes;
    const nodeName = (node: SystemNode | undefined): string => {
      if (!node) return "Unknown component";
      if (node.kind === "zone") return node.label || zoneFallbackLabel;
      if (node.label) return node.label;
      const definition = getDefinition(
        node.definitionId,
        node.customDefinition,
      );
      return definition ? localizedName(definition, locale) : "Component";
    };
    return system.cables.map((c) => {
      let sourceHandle = c.fromTerminalId;
      let targetHandle = c.toTerminalId;

      // Aim at the far end's terminal, not its node centre — a centre can face the other way.
      const fromNode = nodesById[c.fromNodeId];
      const toNode = nodesById[c.toNodeId];

      if (fromNode && isJunction(fromNode)) {
        const jAbs = nodeCenterAbs(fromNode, nodes);
        const wp = c.waypoints?.[0];
        const otherAbs = wp ?? endpointPos(c.toNodeId, c.toTerminalId, nodes);
        if (jAbs && otherAbs) sourceHandle = pickJunctionSide(jAbs, otherAbs);
      }
      if (toNode && isJunction(toNode)) {
        const jAbs = nodeCenterAbs(toNode, nodes);
        const wp = c.waypoints?.[c.waypoints.length - 1];
        const otherAbs = wp ?? endpointPos(c.fromNodeId, c.fromTerminalId, nodes);
        if (jAbs && otherAbs) targetHandle = pickJunctionSide(jAbs, otherAbs);
      }

      return {
        id: c.id,
        source: c.fromNodeId,
        target: c.toNodeId,
        sourceHandle,
        targetHandle,
        type: "cable",
        data: cableEdgeDataById.get(c.id),
        selected: !blueprint && selectedIds.has(c.id),
        ariaLabel:
          c.label || `${nodeName(fromNode)} → ${nodeName(toNode)}`,
        // Above zones, so a cable crossing one isn't hidden behind it.
        zIndex: 2000,
      };
    });
  // Catalog label accessors read only the active locale's label cache.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    system.nodes,
    system.cables,
    nodesById,
    selection.cableIds,
    cableEdgeDataById,
    blueprint,
    locale,
    catalogLabels,
    zoneFallbackLabel,
  ]);

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => {
      const cur = useSystemStore.getState().selection;
      const next = applyNodeSelectionChanges(changes, cur);
      if (next !== cur) setSelection(next);

      for (const change of changes) {
        if (change.type === "position" && change.position && change.dragging) {
          dragNodePosition(change.id, change.position);
        } else if (change.type === "remove") {
          removeNodes([change.id]);
        } else if (change.type === "dimensions" && change.resizing && change.dimensions) {
          // A top/left resize also emits a position change here; without it the anchored edge moves.
          const node = system.nodes.find((n) => n.id === change.id);
          if (node?.kind === "zone") {
            const update: Partial<ZoneNodeT> = {
              width: change.dimensions.width,
              height: change.dimensions.height,
            };
            const posChange = changes.find(
              (c) => c.type === "position" && c.id === change.id && c.position,
            );
            if (posChange && posChange.type === "position" && posChange.position) {
              update.position = posChange.position;
              // Child coords are zone-relative — shift by the inverse delta or they drift.
              const dx = posChange.position.x - node.position.x;
              const dy = posChange.position.y - node.position.y;
              if (dx !== 0 || dy !== 0) {
                for (const child of system.nodes) {
                  if (child.parentId === change.id) {
                    setNodePosition(child.id, {
                      x: child.position.x - dx,
                      y: child.position.y - dy,
                    });
                  }
                }
              }
            }
            updateNode(change.id, update as Partial<ZoneNodeT>);
          }
        }
      }
    },
    [
      dragNodePosition,
      removeNodes,
      setNodePosition,
      setSelection,
      system.nodes,
      updateNode,
    ],
  );

  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      const cur = useSystemStore.getState().selection;
      const next = applyEdgeSelectionChanges(changes, cur);
      if (next !== cur) setSelection(next);

      for (const change of changes) {
        if (change.type === "remove") {
          removeCables([change.id]);
        }
      }
    },
    [removeCables, setSelection],
  );

  // Drive selection through the store: the rfEdges rebuild clobbers anything set via setEdges.
  const onEdgeClick = useCallback(
    (event: React.MouseEvent, edge: Edge) => {
      if (viewOnly) {
        const rect = new DOMRect(event.clientX, event.clientY, 1, 1);
        useCommentPopover.getState().openPopover(
          { kind: "cable", cableId: edge.id },
          rect,
        );
        return;
      }
      const additive = event.shiftKey || event.metaKey || event.ctrlKey;
      const current = useSystemStore.getState().selection;
      if (additive) {
        const next = new Set(current.cableIds);
        if (next.has(edge.id)) next.delete(edge.id);
        else next.add(edge.id);
        setSelection({ nodeIds: current.nodeIds, cableIds: Array.from(next) });
      } else {
        setSelection({ nodeIds: [], cableIds: [edge.id] });
      }
    },
    [setSelection, viewOnly],
  );

  // Deliberately unset — a node tap must not open an empty comment popover in view-only.
  const onNodeClick = undefined;

  // React Flow fires onNodeDragStop for a click with no motion; the tidy must skip those.
  const dragStartPosRef = useRef<{ id: string; x: number; y: number } | null>(
    null,
  );
  const onNodeDragStart = useCallback(
    (_e: unknown, node: Node, nodes: Node[]) => {
      dragStartPosRef.current = {
        id: node.id,
        x: node.position.x,
        y: node.position.y,
      };
      useCanvasDrag
        .getState()
        .setIsDragging(true, nodes.length > 0 ? nodes.map((n) => n.id) : [node.id]);
    },
    [],
  );

  const onNodeDragStop = useCallback(
    (_e: unknown, node: Node, nodes: Node[]) => {
      const start = dragStartPosRef.current;
      dragStartPosRef.current = null;
      useCanvasDrag.getState().setIsDragging(false);
      const moved =
        !start ||
        start.id !== node.id ||
        Math.hypot(node.position.x - start.x, node.position.y - start.y) > 2;
      // All moved nodes: pass only the grabbed one and co-dragged cables keep stale waypoints.
      const movedIds = nodes.length > 0 ? nodes.map((n) => n.id) : [node.id];
      if (moved) {
        for (const id of movedIds) finalizeNodeDrag(id);
      }
      if (moved) {
        requestAnimationFrame(() => {
          tidyCableLayoutsForNodes(movedIds);
          // Runs after the tidy's store write commits, so the caches warm the final system ref.
          queueMicrotask(warmHeavyCaches);
        });
      }

      reparentNodesToZones(movedIds);
    },
    [
      finalizeNodeDrag,
      reparentNodesToZones,
      tidyCableLayoutsForNodes,
      warmHeavyCaches,
    ],
  );

  return (
    <CanvasRenderProfileProvider
      value={{ viewOnly: viewOnly || blueprint, blueprint, showBatteryDetails, connectionOverview, hideCableLabels }}
    >
      <CanvasMetricPillsProvider value={showMetricsOnTouch}>
        <div
          ref={canvasRootRef}
          className="w-full h-full relative"
          data-canvas-presentation={blueprint ? "blueprint" : "editor"}
          data-touch-canvas={
            isTouchLayout && !embedded && !blueprint ? "true" : undefined
          }
        >
      <RoutingSceneContext.Provider value={previewObstacles}>
      <ConnectionPreviewContext.Provider value={connectionPreview}>
      <ReactFlow
        aria-label={ariaLabel}
        nodes={rfNodes}
        edges={rfEdges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onInit={handleInit}
        onMove={handleViewportMove}
        onMoveEnd={handleViewportMoveEnd}
        // Without manual, React Flow lifts a zone child's edges above components.
        zIndexMode="manual"
        nodesDraggable={!viewOnly && !blueprint}
        nodesConnectable={!viewOnly && !blueprint}
        elementsSelectable={!blueprint}
        connectionMode={ConnectionMode.Loose}
        connectOnClick={!viewOnly && !blueprint}
        connectionRadius={
          isTouchLayout ? flowHitTolerance(32, touchZoom) : 20
        }
        connectionDragThreshold={isTouchLayout ? 8 : 1}
        nodeDragThreshold={isTouchLayout ? 8 : 1}
        nodeClickDistance={isTouchLayout ? 8 : 0}
        paneClickDistance={isTouchLayout ? 8 : 1}
        connectionLineComponent={CableConnectionLine}
        onNodesChange={blueprint ? undefined : onNodesChange}
        onEdgesChange={blueprint ? undefined : onEdgesChange}
        onConnect={viewOnly || blueprint ? undefined : onConnect}
        onConnectStart={viewOnly || blueprint ? undefined : onConnectStart}
        onConnectEnd={viewOnly || blueprint ? undefined : onConnectEnd}
        onEdgeClick={blueprint ? undefined : onEdgeClick}
        onPaneClick={
          blueprint
            ? undefined
            : () => setSelection({ nodeIds: [], cableIds: [] })
        }
        onNodeClick={blueprint ? undefined : onNodeClick}
        onNodeDragStart={viewOnly || blueprint ? undefined : onNodeDragStart}
        onNodeDragStop={viewOnly || blueprint ? undefined : onNodeDragStop}
        onDrop={viewOnly || blueprint ? undefined : onDrop}
        onDragOver={viewOnly || blueprint ? undefined : onDragOver}
        proOptions={{ hideAttribution: true }}
        minZoom={blueprint ? 0.01 : minZoom}
        maxZoom={3}
        // Embedded in a page, the pane must give the wheel back or the reader can't scroll past.
        preventScrolling={!embedded && !blueprint}
        snapToGrid={!viewOnly && !blueprint}
        snapGrid={[8, 8]}
        defaultEdgeOptions={{ type: "cable" }}
        panOnDrag={canvasPanOnDrag({
          blueprint,
          viewOnly,
          touch: isTouchLayout,
        })}
        selectionOnDrag={canvasSelectionOnDrag({
          blueprint,
          viewOnly,
          touch: isTouchLayout,
        })}
        zoomOnPinch={!blueprint}
        zoomOnScroll={!blueprint && !embedded}
        zoomOnDoubleClick={!blueprint}
        nodesFocusable={!blueprint && elementsFocusable}
        edgesFocusable={!blueprint && elementsFocusable}
        deleteKeyCode={null}
        selectionKeyCode={null}
        multiSelectionKeyCode={viewOnly ? null : ["Shift", "Meta", "Control"]}
      >
        {!viewOnly && !blueprint && !embedded && (
          <AssistantEditReveal canvasRef={canvasRootRef} touch={isTouchLayout} />
        )}
        {!blueprint && (
          <Background
            variant={BackgroundVariant.Dots}
            gap={16}
            size={1}
            color="hsl(35 8% 80%)"
          />
        )}
        {!blueprint && showControls && (
          <Controls position={controlsPosition} showInteractive={false} />
        )}
        {!blueprint && system.nodes.length > 1 && !viewOnly && !isTouchLayout && (
          <MiniMap
            position="bottom-left"
            pannable
            zoomable
            ariaLabel={tr("canvas.minimap_aria")}
            nodeStrokeWidth={2}
            maskColor="hsl(35 8% 80% / 0.4)"
          />
        )}
      </ReactFlow>
      </ConnectionPreviewContext.Provider>
      </RoutingSceneContext.Provider>
      {!blueprint && canStartSystem && system.nodes.length === 0 && (
        <CanvasEmptyState showPaletteHint={!viewOnly} />
      )}
      {!viewOnly && !blueprint && <GeneratedSystemBanner />}
      {!viewOnly && !blueprint && <PanLockOverlay />}
        </div>
      </CanvasMetricPillsProvider>
    </CanvasRenderProfileProvider>
  );
}

/**
 * `canStartSystem` is separate from `viewOnly`: a share-link reader must never be
 * offered "describe your own system" inside somebody else's diagram.
 */
export function Canvas({
  viewOnly = false,
  canStartSystem = false,
  embedded = false,
  showControls = true,
  autoFit = true,
  showMetricsOnTouch = false,
  showBatteryDetails = true,
  connectionOverview = false,
  hideCableLabels = false,
  initialFitPadding = 0.2,
  initialFitMaxZoom = 1.1,
  minZoom = 0.2,
  ariaLabel,
  elementsFocusable = true,
  controlsPosition = "bottom-right",
  presentation = "editor",
  blueprintModel,
}: {
  viewOnly?: boolean;
  canStartSystem?: boolean;
  embedded?: boolean;
  showControls?: boolean;
  autoFit?: boolean;
  showMetricsOnTouch?: boolean;
  showBatteryDetails?: boolean;
  connectionOverview?: boolean;
  hideCableLabels?: boolean;
  initialFitPadding?: number;
  initialFitMaxZoom?: number;
  minZoom?: number;
  ariaLabel?: string;
  elementsFocusable?: boolean;
  controlsPosition?: "top-left" | "top-right" | "bottom-left" | "bottom-right";
  presentation?: CanvasPresentation;
  blueprintModel?: BlueprintModel;
} = {}) {
  return (
    <CanvasInner
      viewOnly={viewOnly}
      canStartSystem={canStartSystem}
      embedded={embedded}
      showControls={showControls}
      autoFit={autoFit}
      showMetricsOnTouch={showMetricsOnTouch}
      showBatteryDetails={showBatteryDetails}
      connectionOverview={connectionOverview}
      hideCableLabels={hideCableLabels}
      initialFitPadding={initialFitPadding}
      initialFitMaxZoom={initialFitMaxZoom}
      minZoom={minZoom}
      ariaLabel={ariaLabel}
      elementsFocusable={elementsFocusable}
      controlsPosition={controlsPosition}
      presentation={presentation}
      blueprintModel={blueprintModel}
    />
  );
}
